import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Wallet2, RefreshCw, Info, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

/**
 * SalarySummaryWidget — Dashboard tile
 * Category × Month matrix showing Gross + Net salary, with Overtime split out.
 * Data source: GET /api/dashboard/salary-summary?months=N
 * Draft payroll runs are included and marked with a `Draft` badge.
 */
export default function SalarySummaryWidget() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("both");   // both | gross | net

  const load = () => {
    setLoading(true);
    api
      .get(`/dashboard/salary-summary?months=${months}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  const onDownloadExcel = async () => {
    try {
      const res = await api.get(
        `/dashboard/salary-summary/export?months=${months}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Salary_Summary_last_${months}m.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Silent fail — API-level errors are already handled by axios interceptor
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [months]);

  const visibleMonths = useMemo(() => {
    if (!data) return [];
    // Trim leading months with no data to keep the widget compact.
    const arr = data.months;
    const firstIdx = arr.findIndex((m) => m.has_data);
    return firstIdx <= 0 ? arr : arr.slice(firstIdx);
  }, [data]);
  const visibleIdxStart = data && visibleMonths.length ? data.months.length - visibleMonths.length : 0;

  const rowsForTable = useMemo(() => {
    if (!data) return [];
    // Rev. 2026-08-03 — hierarchical rendering: rows are grouped by
    // department. Structure produced here:
    //   [
    //     { kind: "sub_base", dept_name, mode, salary_gross, salary_net },
    //     { kind: "sub_ot",   dept_name, mode, salary_gross, salary_net },
    //     ...   (all Cash/Bank + OT sub-rows for this dept)
    //     { kind: "subtotal", dept_name, salary_gross, salary_net },
    //   ] × N depts
    // Subtotals are pre-computed by the backend (`data.subtotals`).
    const flat = [];
    const subtotalsByDept = {};
    (data.subtotals || []).forEach((s) => { subtotalsByDept[s.dept_id] = s; });
    let zebra = false;
    let lastDept = null;
    data.categories.forEach((c, idx) => {
      if (c.dept_id !== lastDept) {
        // Emit subtotal for previous dept (if any)
        if (lastDept !== null) {
          const prev = subtotalsByDept[lastDept];
          if (prev) {
            flat.push({
              kind: "subtotal",
              label: `${prev.dept_name} — Subtotal`,
              dept_id: lastDept,
              zebra,
              salary_gross: prev.salary_gross.slice(visibleIdxStart)
                .map((v, i) => v + prev.ot_gross.slice(visibleIdxStart)[i]),
              salary_net: prev.salary_net.slice(visibleIdxStart)
                .map((v, i) => v + prev.ot_net.slice(visibleIdxStart)[i]),
            });
          }
        }
        zebra = !zebra;
        lastDept = c.dept_id;
      }
      const modeLbl = c.credit_mode === "unknown" ? "Unknown"
        : c.credit_mode.charAt(0).toUpperCase() + c.credit_mode.slice(1);
      flat.push({
        kind: "sub_base",
        label: `${c.dept_name} — ${modeLbl}`,
        dept_id: c.dept_id,
        zebra,
        salary_gross: c.salary_gross.slice(visibleIdxStart),
        salary_net: c.salary_net.slice(visibleIdxStart),
      });
      flat.push({
        kind: "sub_ot",
        label: `${c.dept_name} — ${modeLbl} — Overtime`,
        dept_id: c.dept_id,
        zebra,
        salary_gross: c.ot_gross.slice(visibleIdxStart),
        salary_net: c.ot_net.slice(visibleIdxStart),
      });
      // Emit subtotal after the last row of this dept
      const isLastOfDept = (idx === data.categories.length - 1)
        || (data.categories[idx + 1].dept_id !== c.dept_id);
      if (isLastOfDept) {
        const sub = subtotalsByDept[c.dept_id];
        if (sub) {
          flat.push({
            kind: "subtotal",
            label: `${sub.dept_name} — Subtotal`,
            dept_id: c.dept_id,
            zebra,
            salary_gross: sub.salary_gross.slice(visibleIdxStart)
              .map((v, i) => v + sub.ot_gross.slice(visibleIdxStart)[i]),
            salary_net: sub.salary_net.slice(visibleIdxStart)
              .map((v, i) => v + sub.ot_net.slice(visibleIdxStart)[i]),
          });
        }
        lastDept = null;  // reset so next iteration doesn't emit again
      }
    });
    return flat;
  }, [data, visibleIdxStart]);

  const totalsForTable = useMemo(() => {
    if (!data) return null;
    return {
      gross: data.totals.gross.slice(visibleIdxStart),
      net: data.totals.net.slice(visibleIdxStart),
    };
  }, [data, visibleIdxStart]);

  const showG = view === "both" || view === "gross";
  const showN = view === "both" || view === "net";

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="salary-summary-widget">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Wallet2 className="h-4 w-4 brand-text" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Salary Summary
              </div>
              <div className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Chivo" }}>
                Department × Month — with Cash / Bank / OT split
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="h-8 text-xs w-[110px]" data-testid="salary-view-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Gross + Net</SelectItem>
                <SelectItem value="gross">Gross only</SelectItem>
                <SelectItem value="net">Net only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
              <SelectTrigger className="h-8 text-xs w-[120px]" data-testid="salary-months-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Last 3 months</SelectItem>
                <SelectItem value="6">Last 6 months</SelectItem>
                <SelectItem value="12">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs px-2"
              onClick={load}
              disabled={loading}
              data-testid="salary-refresh-btn"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs px-2 gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              onClick={onDownloadExcel}
              disabled={loading || !data || !data.categories?.length}
              data-testid="salary-export-btn"
              title="Download this matrix as a formatted Excel file"
            >
              <Download className="h-3 w-3" />
              Excel
            </Button>
          </div>
        </div>

        {!data && !loading && (
          <div className="text-center text-slate-500 text-xs py-6">
            No salary data yet. Approve a payroll run to see numbers here.
          </div>
        )}
        {loading && !data && (
          <div className="text-center text-slate-400 text-xs py-6">Loading…</div>
        )}
        {data && rowsForTable.length === 0 && (
          <div className="text-center text-slate-500 text-xs py-6">
            No payroll data in the selected range.
          </div>
        )}

        {data && rowsForTable.length > 0 && (
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[220px]">
                    Department
                  </th>
                  {visibleMonths.map((m) => (
                    <th
                      key={m.iso}
                      className="text-right px-3 py-2 whitespace-nowrap"
                      data-testid={`salary-col-${m.iso}`}
                    >
                      <div className="font-semibold text-slate-800">{m.label}</div>
                      {m.has_draft && (
                        <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0 rounded bg-amber-100 text-amber-800 font-medium border border-amber-200">
                          Draft
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rowsForTable.map((r, idx) => {
                  // Row style logic — 3 kinds:
                  //   sub_base — dept row with Cash/Bank split (indent 1)
                  //   sub_ot   — italic gray OT sub-row (indent 2)
                  //   subtotal — bold dept subtotal (indent 0, green tint)
                  const isOT = r.kind === "sub_ot";
                  const isSub = r.kind === "subtotal";
                  const zebraTint = r.zebra ? "bg-slate-50/70" : "bg-white";
                  const rowClass = isSub
                    ? "bg-emerald-50 border-t-2 border-emerald-200"
                    : `${zebraTint} hover:bg-slate-100/60`;
                  const cellClass = isSub
                    ? "bg-emerald-50 font-bold text-emerald-900"
                    : isOT
                      ? `${zebraTint} text-slate-500 italic pl-8`
                      : `${zebraTint} font-medium text-slate-800 pl-5`;
                  return (
                    <tr
                      key={idx}
                      className={rowClass}
                      data-testid={`salary-row-${r.kind}-${r.dept_id}-${idx}`}
                    >
                      <td className={`px-3 py-2 sticky left-0 z-10 ${cellClass}`}>
                        {r.label}
                      </td>
                      {r.salary_gross.map((val, i) => (
                        <td
                          key={i}
                          className={`text-right px-3 py-2 tabular-nums whitespace-nowrap ${
                            isSub ? "bg-emerald-50 font-bold text-emerald-900" : ""
                          }`}
                          data-testid={`salary-cell-${idx}-${i}`}
                        >
                          {showG && (
                            <div className={`${
                              val > 0
                                ? isSub ? "text-emerald-900" : "text-slate-900"
                                : "text-slate-300"
                            } ${isOT ? "text-[11px]" : ""} ${isSub ? "font-bold" : ""}`}>
                              {val > 0 ? formatINR(val) : "—"}
                            </div>
                          )}
                          {showN && (
                            <div className={`text-[10px] ${
                              r.salary_net[i] > 0
                                ? isSub ? "text-emerald-700" : "text-slate-500"
                                : "text-slate-300"
                            }`}>
                              {r.salary_net[i] > 0 ? `Net ${formatINR(r.salary_net[i])}` : "—"}
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {totalsForTable && (
                  <tr className="bg-slate-900 text-white font-bold" data-testid="salary-total-row">
                    <td className="px-3 py-2.5 sticky left-0 bg-slate-900 z-10">
                      GRAND TOTAL
                    </td>
                    {totalsForTable.gross.map((v, i) => (
                      <td key={i} className="text-right px-3 py-2.5 tabular-nums whitespace-nowrap">
                        {showG && (
                          <div>{v > 0 ? formatINR(v) : "—"}</div>
                        )}
                        {showN && (
                          <div className="text-[10px] text-slate-300 font-normal">
                            {totalsForTable.net[i] > 0 ? `Net ${formatINR(totalsForTable.net[i])}` : "—"}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {data?.months?.some((m) => m.has_draft) && (
          <div className="mt-2 flex items-start gap-1 text-[11px] text-slate-500">
            <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>
              <b>Draft</b>-marked months include unapproved payroll runs — figures may change until approved.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
